import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, InstagramLogo, FacebookLogo, LinkedinLogo, WhatsappLogo, ArrowRight } from "@phosphor-icons/react";
import { CONTENT, CONTACTS } from "../../data/content";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { Reveal, SectionLabel } from "./Motion";

const initial = { nome: "", cognome: "", email: "", telefono: "", azienda: "", servizio: "", budget: "", descrizione: "" };

export const Contact = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].contact;
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t.success);
      setForm(initial);
    } catch (err) {
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:border-blue-500 focus:outline-none transition-colors";

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-white">{t.title}</h2>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-8 rounded-3xl border border-blue-500/30 bg-gradient-to-r from-blue-500/10 to-transparent p-6 md:p-8">
            <p className="text-zinc-300 leading-relaxed text-sm md:text-base">{t.highlight}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-[#050505] p-6 md:p-8">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="field-nome" className={inputCls} placeholder={t.fields.nome + " *"} value={form.nome} onChange={set("nome")} required />
                <input data-testid="field-cognome" className={inputCls} placeholder={t.fields.cognome} value={form.cognome} onChange={set("cognome")} />
                <input data-testid="field-email" type="email" className={inputCls} placeholder={t.fields.email + " *"} value={form.email} onChange={set("email")} required />
                <input data-testid="field-telefono" className={inputCls} placeholder={t.fields.telefono} value={form.telefono} onChange={set("telefono")} />
                <input data-testid="field-azienda" className={inputCls} placeholder={t.fields.azienda} value={form.azienda} onChange={set("azienda")} />
                <select data-testid="field-servizio" className={inputCls + " appearance-none"} value={form.servizio} onChange={set("servizio")}>
                  <option value="">{t.fields.servizio}</option>
                  {t.servizioOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <select data-testid="field-budget" className={inputCls + " appearance-none sm:col-span-2"} value={form.budget} onChange={set("budget")}>
                  <option value="">{t.fields.budget}</option>
                  {t.budgetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <textarea data-testid="field-descrizione" rows={4} className={inputCls + " sm:col-span-2 resize-none"} placeholder={t.fields.descrizione} value={form.descrizione} onChange={set("descrizione")} />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 text-white font-semibold py-4 hover:bg-blue-400 transition-colors disabled:opacity-60">
                {loading ? t.sending : t.submit} {!loading && <ArrowRight size={18} weight="bold" />}
              </button>
              <p className="mt-4 text-center text-sm text-zinc-500">{t.trust}</p>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-white/10 bg-[#050505] p-6 md:p-8 h-full">
              <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-6">{t.quickTitle}</div>
              <div className="space-y-4">
                <a data-testid="quick-phone" href={`tel:+39${CONTACTS.phone}`} className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors group">
                  <span className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center group-hover:border-blue-500 transition-colors"><Phone size={20} weight="duotone" className="text-blue-400" /></span>
                  {CONTACTS.phoneDisplay}
                </a>
                <a data-testid="quick-whatsapp" href={`https://wa.me/${CONTACTS.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors group">
                  <span className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center group-hover:border-blue-500 transition-colors"><WhatsappLogo size={20} weight="duotone" className="text-blue-400" /></span>
                  WhatsApp
                </a>
                <a data-testid="quick-email" href={`mailto:${CONTACTS.email}`} className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors group break-all">
                  <span className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center group-hover:border-blue-500 transition-colors shrink-0"><EnvelopeSimple size={20} weight="duotone" className="text-blue-400" /></span>
                  {CONTACTS.email}
                </a>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 flex gap-3">
                {[[InstagramLogo, CONTACTS.instagram, "ig", "Instagram"], [FacebookLogo, CONTACTS.facebook, "fb", "Facebook"], [LinkedinLogo, CONTACTS.linkedin, "li", "LinkedIn"]].map(([Icon, url, key, label]) => (
                  <a key={key} data-testid={`social-${key}`} href={url} target="_blank" rel="noreferrer" aria-label={label}
                    className="w-11 h-11 rounded-xl border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-blue-500 transition-colors">
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
